import { useProductDiscovery } from '@/hooks/useProductDiscovery';
import { useUnderwritingSearch } from '@/features/shop-insurance/hooks/use-underwriting-search';
import { useUnderwritingResults } from '@/features/shop-insurance/hooks/use-underwriting-results';
import { useUnderwritingDetailsView } from '@/features/shop-insurance/hooks/use-underwriting-details-view';
import { useSelectedEligibilityFilters } from '@/features/shop-insurance/stores/eligibility-filters-store';
import type { QuoteWithROI, TraditionalLTCQuoteWithROI } from '@/features/shop-insurance/types';

interface UseShopInsuranceUnderwritingParams {
  clientId: string | undefined;
  selectedTab: ShopInsuranceTab;
  adjustedQuotes: QuoteWithROI[];
  traditionalLTCQuotes: TraditionalLTCQuoteWithROI[];
}

export function useShopInsuranceUnderwriting({
  clientId,
  selectedTab,
  adjustedQuotes,
  traditionalLTCQuotes,
}: UseShopInsuranceUnderwritingParams) {
  const selectedEligibilityFilters = useSelectedEligibilityFilters();

  const { productsToSearch } = useProductDiscovery({
    adjustedQuotes,
    traditionalLTCQuotes,
    isJoint: selectedTab === 'joint',
  });

  const {
    fetchStarted: underwritingFetchStarted,
    fetchComplete: underwritingFetchComplete,
    results: searchResults,
  } = useUnderwritingSearch({
    clientId,
    productsToSearch,
    isJoint: selectedTab === 'joint',
    shouldStart: !!clientId && (adjustedQuotes.length > 0 || traditionalLTCQuotes.length > 0),
  });

  const {
    underwritingResults,
    eligibilityCounts,
    filteredAdjustedQuotes,
    filteredTraditionalLTCQuotesWithEligibility,
    showEligibilityFilter,
  } = useUnderwritingResults({
    adjustedQuotes,
    filteredTraditionalLTCQuotes: traditionalLTCQuotes,
    searchResults,
    selectedTab,
    underwritingFetchComplete,
    selectedEligibilityFilters,
  });

  const {
    showUnderwritingSlideOut,
    setShowUnderwritingSlideOut,
    selectedUnderwritingData,
    handleViewUnderwritingDetails,
  } = useUnderwritingDetailsView({ searchResults, selectedTab });

  return {
    underwritingFetchStarted,
    underwritingFetchComplete,
    underwritingResults,
    eligibilityCounts,
    filteredAdjustedQuotes,
    filteredTraditionalLTCQuotesWithEligibility,
    showEligibilityFilter,
    showUnderwritingSlideOut,
    setShowUnderwritingSlideOut,
    selectedUnderwritingData,
    handleViewUnderwritingDetails,
  };
}
